
import React, { useState } from 'react';
import { PlayerProfile, IdolRank } from '../types';
import { ShoppingCart, DollarSign, Hammer } from 'lucide-react';
import { ExitButton } from './ExitButton';
import { Notification } from './NotificationSystem';
import { RANKS } from '../constants';

interface MerchShopProps {
    player: PlayerProfile;
    updatePlayer: (updates: Partial<PlayerProfile>) => void;
    onExit: () => void;
    onNotify: (message: string, type?: Notification['type']) => void;
}

const ALL_FAN_FILES = [
    'fans-01.svg', 'fans-02.html', 'fans-03.svg', 'fans-04.svg', 'fans-05.html',
    'fans-06.html', 'fans-07.png', 'fans-08.png', 'fans-09.png', 'fans-10.png',
    'fans-11.png', 'fans-12.png', 'fans-13.png', 'fans-14.png',
    'fans-15.svg', 'fans-16.html', 'fans-17.svg', 'fans-18.svg', 'fans-19.html', 'fans-20.svg',
    'fans-21.png', 'fans-22.png', 'fans-23.png', 'fans-24.png', 'fans-25.png',
    'fans-26.svg', 'fans-27.svg', 'fans-28.svg', 'fans-29.svg', 'fans-30.svg',
    'fans-31.svg', 'fans-32.svg', 'fans-33.svg', 'fans-34.svg'
];

const FanLineSpot: React.FC<{ filename: string; index: number }> = ({ filename, index }) => {
    const isHtml = filename.endsWith('.html');
    const path = `/fans/${filename}`;

    return (
        <div
            className="relative flex flex-col items-center group w-full max-w-2xl mb-32"
            style={{
                marginTop: '4rem',
                zIndex: 100 - index
            }}
        >
            {/* Shadow/Glow under fan */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-[600px] bg-blue-500/5 blur-3xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></div>

            <div className="relative w-full flex items-center justify-center overflow-visible">
                {isHtml ? (
                    <iframe
                        src={path}
                        className="w-full h-[600px] border-none pointer-events-none overflow-hidden"
                        title={`Fan ${index + 1}`}
                        scrolling="no"
                    />
                ) : (
                    <img
                        src={path}
                        alt={`Fan Line Spot ${index + 1}`}
                        className="w-full h-auto object-cover filter drop-shadow-[0_0_20px_rgba(59,130,246,0.4)] transition-transform group-hover:scale-105"
                    />
                )}
            </div>

            {/* Fan Line Spot Label */}
            <div className="mt-2 text-[8px] font-mono text-blue-400/40 uppercase tracking-tighter opacity-0 group-hover:opacity-100 transition-opacity">
                Awaiting Merch #{String(index + 1).padStart(2, '0')}
            </div>
        </div>
    );
};

export const MerchShop: React.FC<MerchShopProps> = ({ player, updatePlayer, onExit, onNotify }) => {
    // Loretta: Merch and Scraps are highly valued by fans
    const rankData = RANKS[player.rank] || RANKS[IdolRank.TRAINEE];
    const multiplier = rankData?.multiplier || 1;
    const PRICE_PER_ITEM = (2500 + Math.floor(player.skills.marketing * 150)) * multiplier;

    // Use state to shuffle only once on mount
    const [shuffledFans] = useState(() => {
        const array = [...ALL_FAN_FILES];
        for (let i = array.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [array[i], array[j]] = [array[j], array[i]];
        }
        return array;
    });

    const sellMerch = () => {
        if (player.merchStock > 0) {
            const value = player.merchStock * PRICE_PER_ITEM;
            updatePlayer({
                cash: player.cash + value,
                merchStock: 0
            });
            onNotify(`Sold ${player.merchStock} units of Merch and Jet Salvage Scraps for $${value.toLocaleString()}!`, 'success');
        }
    };

    return (
        <div className="h-full w-full bg-slate-950 overflow-y-auto overflow-x-hidden flex flex-col items-center p-8 relative scrollbar-hide">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-blue-900/10 via-slate-950 to-slate-950 fixed"></div>
            <ExitButton onExit={onExit} />

            <div className="relative z-10 w-full max-w-2xl glass-panel p-8 rounded-lg shrink-0">
                <div className="text-center mb-8">
                    <h2 className="text-3xl font-heading text-blue-400 mb-2">HUNTR/X COLLECTIBLES</h2>
                    <div className="text-blue-200/60">"Official Merch & Authentic Jet Wreckage Scraps"</div>
                </div>

                <div className="grid grid-cols-1 gap-6">
                    <div className="bg-slate-800/50 p-6 rounded-lg border border-slate-700 hover:border-blue-600 transition-colors flex flex-col items-center text-center">
                        <div className="flex gap-4 mb-4">
                            <ShoppingCart size={48} className="text-blue-400" />
                            <Hammer size={48} className="text-pink-500" />
                        </div>
                        <h3 className="text-xl font-bold text-white mb-2">Process Inventory</h3>
                        <p className="text-slate-400 text-sm mb-4 leading-relaxed">
                            Fans are desperate for scraps of the jet that crashed during the last tour.<br />
                            <span className="text-pink-400 font-bold">In-Stock items: {player.merchStock}</span><br />
                            Current Value: <span className="text-green-400">${PRICE_PER_ITEM.toLocaleString()}</span> per unit
                        </p>
                        <button
                            onClick={sellMerch}
                            disabled={player.merchStock <= 0}
                            className="w-full py-4 bg-blue-700 hover:bg-blue-600 disabled:bg-slate-700 disabled:text-slate-500 text-white font-bold rounded-md flex items-center justify-center gap-2"
                        >
                            <DollarSign /> SELL ALL FOR ${(player.merchStock * PRICE_PER_ITEM).toLocaleString()}
                        </button>
                    </div>
                </div>


            </div>

            {/* The Crowd Waiting in Line */}
            <div className="relative z-10 flex flex-col items-center pb-32 mt-4">
                <div className="text-blue-400/50 text-3xl uppercase tracking-[0.2em] mb-12 font-black italic drop-shadow-[0_0_15px_rgba(96,165,250,0.4)] text-center">
                    Fan Line Starts Here: Scroll Down For The Fans...
                </div>

                {shuffledFans.map((filename, i) => (
                    <FanLineSpot key={filename} filename={filename} index={i} />
                ))}

                {/* Ground/Shadow at the very end of line */}
                <div className="w-64 h-8 bg-blue-900/5 blur-2xl mt-8"></div>
            </div>
        </div>
    );
};
